import { ModalInstance } from './types';

/**
 * Singleton class to manage multiple modal instances
 * Handles z-index stacking and focus management
 */
class ModalManager {
  private static instance: ModalManager;
  private modals: Map<string, ModalInstance> = new Map();
  private baseZIndex = 9000;
  private currentZIndex = this.baseZIndex;

  static getInstance(): ModalManager {
    if (!ModalManager.instance) {
      ModalManager.instance = new ModalManager();
    }
    return ModalManager.instance;
  }

  /**
   * Register a new modal instance
   */
  registerModal(id: string): number {
    const zIndex = ++this.currentZIndex;
    
    // Deactivate all other modals
    this.modals.forEach((modal) => {
      modal.isActive = false;
    });

    // Register new modal as active
    this.modals.set(id, {
      id,
      zIndex,
      isActive: true
    });

    return zIndex;
  }

  /**
   * Unregister a modal instance
   */
  unregisterModal(id: string): void {
    this.modals.delete(id);
    
    // If this was the active modal, activate the next highest
    const remainingModals = Array.from(this.modals.values())
      .sort((a, b) => b.zIndex - a.zIndex);
    
    if (remainingModals.length > 0) {
      remainingModals[0].isActive = true;
    }
  }

  /**
   * Bring a modal to the front
   */
  bringToFront(id: string): number {
    const modal = this.modals.get(id);
    if (!modal) return this.baseZIndex;

    // If already active, no need to change
    if (modal.isActive) return modal.zIndex;

    // Deactivate all modals
    this.modals.forEach((m) => {
      m.isActive = false;
    });

    // Give this modal a new z-index and make it active
    const newZIndex = ++this.currentZIndex;
    modal.zIndex = newZIndex;
    modal.isActive = true;

    return newZIndex;
  }

  /**
   * Get the z-index for a modal
   */
  getZIndex(id: string): number {
    const modal = this.modals.get(id);
    return modal ? modal.zIndex : this.baseZIndex;
  }

  /**
   * Check if a modal is active (topmost)
   */
  isActive(id: string): boolean {
    const modal = this.modals.get(id);
    return modal ? modal.isActive : false;
  }

  /**
   * Get all registered modal IDs
   */
  getAllModalIds(): string[] {
    return Array.from(this.modals.keys());
  }

  /**
   * Get the count of registered modals
   */
  getModalCount(): number {
    return this.modals.size;
  }

  /**
   * Reset the manager (useful for testing)
   */
  reset(): void {
    this.modals.clear();
    this.currentZIndex = this.baseZIndex;
  }
}

export default ModalManager;
